<script setup lang="ts">
import { ref, computed, provide, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  RBtn,
  useLoadingSpinnerStore,
} from '@therobotcompany/robco-frontend-library';
import type {
  CatalogueItem,
  CatalogueItemGroup,
  CatalogueItemDetail,
  ChildItemData,
} from '@/models/Catalogue';
import gettext from '@/i18n/gettext';
import {
  createCatalogueItem,
  updateCatalogueItem,
  deleteCatalogueItem,
  duplicateCatalogueItem,
  createItemRevision,
  uploadItemImage,
  deleteItemImage,
  uploadItemFile,
  deleteItemFile,
  getCatalogueGroups,
  createCatalogueGroup,
  updateCatalogueGroup,
  deleteCatalogueGroup,
  getCatalogueItem,
  getItemChildren,
  addItemChild,
  updateItemChildQuantity,
  removeItemChild,
  submitPromotionRequest,
  cancelPromotionRequest,
  getErpSyncStatus,
} from '@/api/CatalogueApi';
import { getProjects, getProjectBoms } from '@/api/ProjectsApi';
import { buildCatalogueGroupHierarchy } from '@/composables/catalogue/useCatalogueGroupHierarchy';
import CatalogueItemBrowser from '@/components/catalogue/CatalogueItemBrowser.vue';
import CatalogueItemDialog from '@/components/catalogue/CatalogueItemDialog.vue';
import CatalogueItemDeleteDialog from '@/components/catalogue/CatalogueItemDeleteDialog.vue';
import CatalogueGroupDialog from '@/components/catalogue/CatalogueGroupDialog.vue';
import CatalogueGroupDeleteDialog from '@/components/catalogue/CatalogueGroupDeleteDialog.vue';
import CatalogueItemViewDialog from '@/components/catalogue/CatalogueItemViewDialog.vue';
import ManageGroupsDialog from '@/components/catalogue/ManageGroupsDialog.vue';
import BomDialog from '@/components/projects/BomDialog.vue';
import ProjectsSidebar from '@/components/projects/ProjectsSidebar.vue';
import PromotionQueueDialog from '@/components/catalogue/PromotionQueueDialog.vue';
import ErpDialog from '@/components/catalogue/ErpDialog.vue';
import ErpSyncStatusDialog from '@/components/catalogue/ErpSyncStatusDialog.vue';
import { useBomStore } from '@/store/BomStore';
import { useAlertsStore } from '@/store/AlertsStore';
import useAuthStore from '@/store/AuthStore';
import { Permission } from '@/models/Roles';
import type { CatalogueProject, CatalogueProjectBom } from '@/models/Projects';
import { CatalogueItemActionsKey } from '@/composables/catalogue/useCatalogueItemActions';
import { getErpSyncColor } from '@/helpers/ErpSyncHelper';

const { $gettext } = gettext;
const loadingSpinnerStore = useLoadingSpinnerStore();
const bomStore = useBomStore();
const alerts = useAlertsStore();
const authStore = useAuthStore();
const route = useRoute();
const router = useRouter();

const isPowerUser = authStore.hasPermission(Permission.UPDATE_CATALOGUE_ALL);
const canEditCatalogue = authStore.hasPermission(Permission.UPDATE_CATALOGUE);

// Reference to the browser component for reloading
const browserRef = ref<InstanceType<typeof CatalogueItemBrowser> | null>(null);

const groups = ref<CatalogueItemGroup[]>([]);
const selectedItem = ref<CatalogueItemDetail | undefined>(undefined);
const showDeleteDialog = ref(false);
const itemToDelete = ref<CatalogueItem | null>(null);
const showFormDialog = ref(false);
const showGroupDialog = ref(false);
const groupToEdit = ref<CatalogueItemGroup | undefined>(undefined);
const showGroupDeleteDialog = ref(false);
const groupToDelete = ref<CatalogueItemGroup | null>(null);
const showViewDialog = ref(false);
const showManageGroupsDialog = ref(false);
const showPromotionQueueDialog = ref(false);
const showErpDialog = ref(false);
const showErpSyncStatusDialog = ref(false);
const itemToAddToBom = ref<CatalogueItem | null>(null);

// ERP sync status
const erpSyncTimestamp = ref<string | null>(null);

const erpSyncIconColor = computed(() =>
  getErpSyncColor(erpSyncTimestamp.value),
);

const loadErpSyncStatus = async () => {
  try {
    erpSyncTimestamp.value = await getErpSyncStatus();
  } catch (error) {
    console.error('Failed to load ERP sync status:', error);
  }
};

// Floating Projects window state
const showProjectsWindow = ref(false);

const handleSelectBomFromSidebar = async (
  project: CatalogueProject,
  bom: CatalogueProjectBom,
) => {
  await bomStore.setSelectedProjectAndBom(project, bom);
};

// Helper to reload items via the browser component
const reloadItems = () => {
  browserRef.value?.reload();
};

const loadGroups = async () => {
  loadingSpinnerStore.setShowLoadingOverlay(true);
  try {
    const flatGroups = await getCatalogueGroups();
    groups.value = buildCatalogueGroupHierarchy(flatGroups);
  } catch (err) {
    alerts.error($gettext('Failed to load catalogue groups'));
    console.error(err);
  } finally {
    loadingSpinnerStore.setShowLoadingOverlay(false);
  }
};

const handleSelectItem = async (item: CatalogueItem) => {
  loadingSpinnerStore.setShowLoadingOverlay(true);
  try {
    // Fetch item details and children in parallel
    const [itemDetails, children] = await Promise.all([
      getCatalogueItem(item.uuid),
      getItemChildren(item.uuid),
    ]);

    // Combine the data
    selectedItem.value = {
      ...itemDetails,
      children,
    };
    showViewDialog.value = true;
  } catch (err) {
    alerts.error($gettext('Failed to load item details'));
    console.error(err);
    selectedItem.value = undefined;
  } finally {
    loadingSpinnerStore.setShowLoadingOverlay(false);
  }
};

const handleCreate = () => {
  selectedItem.value = undefined;
  showFormDialog.value = true;
};

const handleEdit = async (item: CatalogueItem) => {
  await handleSelectItem(item);
  if (selectedItem.value) {
    showFormDialog.value = true;
  }
};

const handleDelete = async (item: CatalogueItem) => {
  itemToDelete.value = item;
  showDeleteDialog.value = true;
};

const handleDuplicate = async (item: CatalogueItem) => {
  loadingSpinnerStore.setShowLoadingOverlay(true);
  try {
    const duplicatedItem = await duplicateCatalogueItem(item.uuid);

    // Ensure duplicated category is always 'custom'
    const ensured = await updateCatalogueItem(duplicatedItem.uuid, {
      ...duplicatedItem,
      category: 'custom',
    } as CatalogueItem);

    // Fetch the full details of the duplicated item
    const [itemDetails, children] = await Promise.all([
      getCatalogueItem(ensured.uuid),
      getItemChildren(ensured.uuid),
    ]);

    // Set the duplicated item as selected and open the view dialog
    selectedItem.value = {
      ...itemDetails,
      children,
    };
    showViewDialog.value = true;

    // Reload list
    reloadItems();
  } catch (err) {
    alerts.error($gettext('Failed to duplicate item'));
    console.error(err);
  } finally {
    loadingSpinnerStore.setShowLoadingOverlay(false);
  }
};

const handleCreateRevision = async (item: CatalogueItem) => {
  loadingSpinnerStore.setShowLoadingOverlay(true);
  try {
    const revision = await createItemRevision(item.uuid);

    const [itemDetails, children] = await Promise.all([
      getCatalogueItem(revision.uuid),
      getItemChildren(revision.uuid),
    ]);

    selectedItem.value = { ...itemDetails, children };
    showViewDialog.value = true;
    reloadItems();
    alerts.success($gettext('Revision created'));
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (e: any) {
    console.error(e);
    if (e.response?.status === 400) {
      alerts.error($gettext('Only standard items can be versioned'));
    } else if (e.response?.status === 409) {
      alerts.error($gettext('Item already has a next version'));
    } else {
      alerts.error($gettext('Failed to create revision'));
    }
  } finally {
    loadingSpinnerStore.setShowLoadingOverlay(false);
  }
};

const handleAddToBom = async (item: CatalogueItem) => {
  itemToAddToBom.value = item;

  // Check if a BOM is already selected
  if (bomStore.hasSelectedProjectAndBom) {
    await bomStore.addItem(item, 1);
  } else {
    showProjectsWindow.value = true;
  }
};

const handleOpenChild = async (uuid: string) => {
  loadingSpinnerStore.setShowLoadingOverlay(true);
  try {
    const [itemDetails, children] = await Promise.all([
      getCatalogueItem(uuid),
      getItemChildren(uuid),
    ]);
    selectedItem.value = { ...itemDetails, children };
    showViewDialog.value = true;
  } catch (err) {
    alerts.error($gettext('Failed to load item details'));
    console.error(err);
  } finally {
    loadingSpinnerStore.setShowLoadingOverlay(false);
  }
};

const handleOpenItemFromBom = async (uuid: string) => {
  await handleOpenChild(uuid);
};

const confirmDelete = async () => {
  if (!itemToDelete.value) {
    return;
  }

  loadingSpinnerStore.setShowLoadingOverlay(true);
  try {
    await deleteCatalogueItem(itemToDelete.value.uuid);

    reloadItems();
    if (selectedItem.value?.uuid === itemToDelete.value.uuid) {
      selectedItem.value = undefined;
      showViewDialog.value = false;
    }
    alerts.success($gettext('Item deleted'));
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (e: any) {
    console.error(e);
    if (e.response?.status === 400 && e.response?.data?.detail === 'child') {
      alerts.error(
        $gettext(
          'Cannot delete item because it is a component of another. Remove it first',
        ),
      );
    } else if (
      e.response?.status === 400 &&
      e.response?.data?.detail === 'bom'
    ) {
      alerts.error(
        $gettext(
          'Cannot delete item because it is part of a BOM. Remove it first',
        ),
      );
    } else if (e.response?.status === 409) {
      alerts.error(
        $gettext(
          'Cannot delete item because it has a newer version. Delete the newer version first',
        ),
      );
    } else {
      alerts.error($gettext('Failed to delete Item'));
    }
  } finally {
    loadingSpinnerStore.setShowLoadingOverlay(false);
    showDeleteDialog.value = false;
    itemToDelete.value = null;
  }
};

const handleSave = async (item: CatalogueItem) => {
  loadingSpinnerStore.setShowLoadingOverlay(true);
  try {
    if (item.uuid) {
      const updatedItem = await updateCatalogueItem(item.uuid, item);
      if (selectedItem.value && selectedItem.value.uuid === item.uuid) {
        selectedItem.value = { ...selectedItem.value, ...updatedItem };
      }
      bomStore.applyUpdatedItem(updatedItem);
      alerts.success($gettext('Item updated'));
    } else {
      const created = await createCatalogueItem(item);
      // Reset browser to first page when adding new item
      browserRef.value?.reset();

      // Show pop up of item on creation
      const [itemDetails, children] = await Promise.all([
        getCatalogueItem(created.uuid),
        getItemChildren(created.uuid),
      ]);
      selectedItem.value = { ...itemDetails, children };
      showViewDialog.value = true;
      alerts.success($gettext('Item created'));
    }

    // Reload page on creation/duplicate/update of an item
    reloadItems();
    showFormDialog.value = false;
  } catch (err) {
    alerts.error($gettext('Failed to save item'));
    console.error(err);
  } finally {
    loadingSpinnerStore.setShowLoadingOverlay(false);
  }
};

const handleImageUpload = async (file: File) => {
  if (!selectedItem.value) {
    return;
  }

  loadingSpinnerStore.setShowLoadingOverlay(true);
  try {
    const { images } = await uploadItemImage(selectedItem.value.uuid, file);
    if (selectedItem.value) {
      selectedItem.value.images = images;
    }
    // Reload browser to reflect image changes in the grid
    reloadItems();
  } catch (err) {
    alerts.error($gettext('Failed to upload image'));
    console.error(err);
  } finally {
    loadingSpinnerStore.setShowLoadingOverlay(false);
  }
};

const handleImageDelete = async (imageUrl: string) => {
  if (!selectedItem.value) {
    return;
  }

  loadingSpinnerStore.setShowLoadingOverlay(true);
  try {
    const { images } = await deleteItemImage(selectedItem.value.uuid, imageUrl);
    if (selectedItem.value) {
      selectedItem.value.images = images;
    }
    // Reload browser to reflect image changes in the grid
    reloadItems();
  } catch (err) {
    alerts.error($gettext('Failed to delete image'));
    console.error(err);
  } finally {
    loadingSpinnerStore.setShowLoadingOverlay(false);
  }
};

const handleFileUpload = async (file: File) => {
  if (!selectedItem.value) {
    return;
  }

  loadingSpinnerStore.setShowLoadingOverlay(true);
  try {
    const { files } = await uploadItemFile(selectedItem.value.uuid, file);
    if (selectedItem.value) {
      selectedItem.value.files = files;
    }
  } catch (err) {
    alerts.error($gettext('Failed to upload file'));
    console.error(err);
  } finally {
    loadingSpinnerStore.setShowLoadingOverlay(false);
  }
};

const handleFileDelete = async (fileName: string) => {
  if (!selectedItem.value) {
    return;
  }

  loadingSpinnerStore.setShowLoadingOverlay(true);
  try {
    const { files } = await deleteItemFile(selectedItem.value.uuid, fileName);
    if (selectedItem.value) {
      selectedItem.value.files = files;
    }
  } catch (err) {
    alerts.error($gettext('Failed to delete file'));
    console.error(err);
  } finally {
    loadingSpinnerStore.setShowLoadingOverlay(false);
  }
};

const handleCreateGroup = () => {
  groupToEdit.value = undefined;
  showGroupDialog.value = true;
};

const handleEditGroup = (group: CatalogueItemGroup) => {
  groupToEdit.value = group;
  showGroupDialog.value = true;
};

const handleDeleteGroup = async (group: CatalogueItemGroup) => {
  groupToDelete.value = group;
  showGroupDeleteDialog.value = true;
};

const confirmDeleteGroup = async () => {
  if (!groupToDelete.value) {
    return;
  }

  loadingSpinnerStore.setShowLoadingOverlay(true);
  try {
    await deleteCatalogueGroup(groupToDelete.value.uuid);
    await loadGroups();
  } catch (err) {
    alerts.error($gettext('Failed to delete group'));
    console.error(err);
  } finally {
    loadingSpinnerStore.setShowLoadingOverlay(false);
    showGroupDeleteDialog.value = false;
    groupToDelete.value = null;
  }
};

const handleSaveGroup = async (data: {
  groupData: Partial<CatalogueItemGroup>;
  groupToEdit?: CatalogueItemGroup;
}) => {
  loadingSpinnerStore.setShowLoadingOverlay(true);
  try {
    if (data.groupToEdit) {
      if (data.groupToEdit?.uuid) {
        await updateCatalogueGroup(
          data.groupToEdit.uuid,
          data.groupData as CatalogueItemGroup,
        );
      }
    } else {
      await createCatalogueGroup(data.groupData as CatalogueItemGroup);
    }

    await loadGroups();
    showGroupDialog.value = false;
  } catch (err) {
    alerts.error($gettext('Failed to save group'));
    console.error(err);
  } finally {
    loadingSpinnerStore.setShowLoadingOverlay(false);
  }
};

const handleAddChild = async (childData: ChildItemData) => {
  if (!selectedItem.value) {
    return;
  }
  loadingSpinnerStore.setShowLoadingOverlay(true);
  try {
    const updatedChildren = await addItemChild(
      selectedItem.value.uuid,
      childData,
    );
    if (selectedItem.value) {
      selectedItem.value.children = updatedChildren;
    }
  } catch (err) {
    alerts.error($gettext('Failed to add item component'));
    console.error(err);
  } finally {
    loadingSpinnerStore.setShowLoadingOverlay(false);
  }
};

const handleUpdateChildQuantity = async (
  childUuid: string,
  quantity: number,
) => {
  if (!selectedItem.value) {
    return;
  }
  loadingSpinnerStore.setShowLoadingOverlay(true);
  try {
    const updatedChildren = await updateItemChildQuantity(
      selectedItem.value.uuid,
      childUuid,
      quantity,
    );
    if (selectedItem.value) {
      selectedItem.value.children = updatedChildren;
    }
  } catch (err) {
    alerts.error($gettext('Failed to update component quantity'));
    console.error(err);
  } finally {
    loadingSpinnerStore.setShowLoadingOverlay(false);
  }
};

const handleRemoveChild = async (childUuid: string) => {
  if (!selectedItem.value) {
    return;
  }
  loadingSpinnerStore.setShowLoadingOverlay(true);
  try {
    const updatedChildren = await removeItemChild(
      selectedItem.value.uuid,
      childUuid,
    );
    if (selectedItem.value) {
      selectedItem.value.children = updatedChildren;
    }
  } catch (err) {
    alerts.error($gettext('Failed to remove item component'));
    console.error(err);
  } finally {
    loadingSpinnerStore.setShowLoadingOverlay(false);
  }
};

const handleRequestPromotion = async (item: CatalogueItemDetail) => {
  loadingSpinnerStore.setShowLoadingOverlay(true);
  try {
    const promotionRequest = await submitPromotionRequest(item.uuid);
    if (selectedItem.value && selectedItem.value.uuid === item.uuid) {
      selectedItem.value.promotionRequest = promotionRequest;
    }
    alerts.success($gettext('Promotion request submitted'));
  } catch (err) {
    alerts.error($gettext('Failed to submit promotion request'));
    console.error(err);
  } finally {
    loadingSpinnerStore.setShowLoadingOverlay(false);
  }
};

const handleCancelPromotion = async (item: CatalogueItemDetail) => {
  if (!item.promotionRequest) {
    return;
  }
  loadingSpinnerStore.setShowLoadingOverlay(true);
  try {
    await cancelPromotionRequest(item.promotionRequest.uuid);
    if (selectedItem.value && selectedItem.value.uuid === item.uuid) {
      selectedItem.value.promotionRequest = undefined;
    }
    alerts.success($gettext('Promotion request cancelled'));
  } catch (err) {
    alerts.error($gettext('Failed to cancel promotion request'));
    console.error(err);
  } finally {
    loadingSpinnerStore.setShowLoadingOverlay(false);
  }
};

const handleErpImported = () => {
  reloadItems();
  loadErpSyncStatus();
};

const handlePromotionReviewed = () => {
  // Reload items to reflect any status changes (e.g., item becoming standard)
  reloadItems();
  // Refresh selected item if open
  if (selectedItem.value) {
    handleOpenChild(selectedItem.value.uuid);
  }
};

provide(CatalogueItemActionsKey, {
  viewItem: handleSelectItem,
  editItem: handleEdit,
  deleteItem: handleDelete,
  duplicateItem: handleDuplicate,
  createRevision: handleCreateRevision,
  addToBom: handleAddToBom,
  openChild: handleOpenChild,
  requestPromotion: handleRequestPromotion,
  cancelPromotion: handleCancelPromotion,
  uploadImage: handleImageUpload,
  deleteImage: handleImageDelete,
  uploadFile: handleFileUpload,
  deleteFile: handleFileDelete,
  addChild: handleAddChild,
  removeChild: handleRemoveChild,
  updateChildQuantity: handleUpdateChildQuantity,
});

const applyItemDeepLinkFromRoute = async () => {
  const itemUuid = (route.query.item as string) || '';

  try {
    if (itemUuid) {
      await handleOpenChild(itemUuid);
    }
  } catch (e) {
    console.error('Failed to apply item from URL:', e);
  }
};

const applyBomDeepLinkFromRoute = async () => {
  const projectId = route.query.projectId as string;
  const bomId = route.query.bomId as string;

  if (!projectId || !bomId) {
    return;
  }

  try {
    const projects = await getProjects();
    const project = projects.find((p) => p.uuid === projectId);
    if (!project) {
      alerts.error($gettext('Project from URL not found'));
      return;
    }

    const boms = await getProjectBoms(projectId);
    const bom = boms.find((b) => b.uuid === bomId);
    if (!bom) {
      alerts.error($gettext('BOM from URL not found'));
      return;
    }

    await bomStore.setSelectedProjectAndBom(project, bom);
  } catch (e) {
    console.error('Failed to apply BOM deep link:', e);
    alerts.error($gettext('Failed to load BOM from URL'));
  }
};

onMounted(() => {
  loadGroups();
  loadErpSyncStatus();
  applyItemDeepLinkFromRoute();
  applyBomDeepLinkFromRoute();
});

watch(
  () => showViewDialog.value,
  (open) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const newQuery = { ...route.query } as Record<string, any>;
    if (open && selectedItem.value?.uuid) {
      newQuery.item = selectedItem.value.uuid;
    } else {
      delete newQuery.item;
    }
    router.replace({ query: newQuery });
  },
);

watch(
  () => [bomStore.selectedProject, bomStore.selectedBom],
  ([project, bom]) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const newQuery = { ...route.query } as Record<string, any>;
    if (project && bom) {
      newQuery.projectId = project.uuid;
      newQuery.bomId = bom.uuid;
    } else {
      delete newQuery.projectId;
      delete newQuery.bomId;
    }
    router.replace({ query: newQuery });
  },
);
</script>

<template>
  <div class="pa-4 pa-sm-12">
    <!-- Toggle button stays next to sidebar -->
    <div class="projects-container">
      <transition name="slide-x">
        <div v-if="showProjectsWindow" class="projects-sidebar">
          <ProjectsSidebar
            @select-bom="handleSelectBomFromSidebar"
            @close="showProjectsWindow = false"
          />
        </div>
      </transition>

      <r-btn
        class="projects-toggle"
        :icon="showProjectsWindow ? 'mdi-chevron-right' : 'mdi-chevron-left'"
        :aria-label="
          showProjectsWindow
            ? $gettext('Close Projects')
            : $gettext('Open Projects')
        "
        @click="showProjectsWindow = !showProjectsWindow"
      >
        {{ $gettext('Projects') }}
      </r-btn>
    </div>

    <!-- Main content -->
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <div class="d-flex align-center mb-4">
            <h1>{{ $gettext('Catalogue') }}</h1>
          </div>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <div class="d-flex align-center mb-4">
            <r-btn
              v-if="isPowerUser"
              class="mr-2"
              variant="outlined"
              icon="mdi-folder-cog-outline"
              @click="showManageGroupsDialog = true"
            >
              {{ $gettext('Manage Groups') }}
            </r-btn>
            <r-btn
              v-if="canEditCatalogue || isPowerUser"
              class="mr-2"
              variant="outlined"
              icon="mdi-clipboard-check-outline"
              @click="showPromotionQueueDialog = true"
            >
              {{ $gettext('Promotion Queue') }}
            </r-btn>
            <r-btn
              class="mr-2"
              variant="outlined"
              @click="showErpSyncStatusDialog = true"
            >
              <v-icon :color="erpSyncIconColor" class="mr-1">
                mdi-circle
              </v-icon>
              {{ $gettext('ERP Sync') }}
            </r-btn>
            <r-btn
              v-if="isPowerUser"
              class="mr-2"
              variant="outlined"
              icon="mdi-database-import-outline"
              @click="showErpDialog = true"
            >
              {{ $gettext('ERP Import') }}
            </r-btn>
            <r-btn variant="outlined" icon="mdi-plus" @click="handleCreate">
              {{ $gettext('Add Item') }}
            </r-btn>
          </div>
        </v-col>
      </v-row>

      <!-- BOM Status Display -->
      <v-row class="mb-4">
        <v-col cols="12">
          <v-alert
            v-if="bomStore.hasSelectedProjectAndBom"
            color="info"
            variant="tonal"
            class="mb-4"
          >
            <div class="d-flex align-center justify-space-between">
              <div>
                <strong>{{ $gettext('Current BOM') }}:</strong>
                {{ bomStore.selectedProject?.name }} -
                {{ bomStore.selectedBom?.name }}
                <span class="text-caption ml-2">
                  ({{ bomStore.totalItems }} {{ $gettext('items') }})
                </span>
              </div>
              <div class="d-flex align-right justify-space-between">
                <r-btn
                  variant="outlined"
                  icon="mdi-file-tree"
                  class="mr-2"
                  @click="bomStore.showBomDialog"
                >
                  {{ $gettext('View BOM') }}
                </r-btn>
                <r-btn
                  variant="outlined"
                  icon="mdi-broom"
                  @click="bomStore.clearSelection()"
                >
                  {{ $gettext('Clear Selected BOM') }}
                </r-btn>
              </div>
            </div>
          </v-alert>
          <v-alert v-else color="warning" variant="tonal" class="mb-4">
            <div class="d-flex align-center justify-space-between">
              <div>
                <strong>{{ $gettext('No BOM Selected') }}</strong>
                <span class="text-caption ml-2">
                  {{ $gettext('You must select a BOM before adding items.') }}
                </span>
              </div>
              <r-btn
                variant="outlined"
                icon="mdi-file-tree"
                @click="showProjectsWindow = true"
              >
                {{ $gettext('Select BOM') }}
              </r-btn>
            </div>
          </v-alert>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <CatalogueItemBrowser ref="browserRef" :groups="groups" />
        </v-col>
      </v-row>
    </v-container>

    <ManageGroupsDialog
      v-model="showManageGroupsDialog"
      :groups="groups"
      @create="handleCreateGroup"
      @edit="handleEditGroup"
      @delete="handleDeleteGroup"
      @saveGroup="handleSaveGroup"
    />

    <CatalogueItemViewDialog
      v-model="showViewDialog"
      :item="selectedItem"
      :groups="groups"
    />

    <CatalogueItemDialog
      v-model="showFormDialog"
      :item="selectedItem"
      :groups="groups"
      @save="handleSave"
    />

    <CatalogueItemDeleteDialog
      v-model="showDeleteDialog"
      :item="itemToDelete"
      @confirm="confirmDelete"
    />

    <CatalogueGroupDialog
      v-model="showGroupDialog"
      :group="groupToEdit"
      :groups="groups"
      @save="
        (groupData) => handleSaveGroup({ groupData, groupToEdit: groupToEdit })
      "
    />

    <CatalogueGroupDeleteDialog
      v-model="showGroupDeleteDialog"
      :group="groupToDelete"
      @confirm="confirmDeleteGroup"
    />

    <BomDialog @open-item="handleOpenItemFromBom" />

    <PromotionQueueDialog
      v-model="showPromotionQueueDialog"
      :can-review="isPowerUser"
      @reviewed="handlePromotionReviewed"
      @open-item="handleOpenChild"
    />

    <ErpDialog
      v-model="showErpDialog"
      :groups="groups"
      @imported="handleErpImported"
    />

    <ErpSyncStatusDialog
      v-model="showErpSyncStatusDialog"
      :sync-timestamp="erpSyncTimestamp"
      @refresh-sync="loadErpSyncStatus"
    />
  </div>
</template>

<style lang="scss" scoped>
.projects-container {
  position: fixed;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  z-index: 2000;
}

.projects-sidebar {
  width: 90vw;
  max-width: 1100px;
  height: 80vh;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* Button always beside the sidebar */
.projects-toggle {
  margin-left: 8px;
  flex-shrink: 0;
}

/* Simple horizontal slide animation */
.slide-x-enter-active,
.slide-x-leave-active {
  transition:
    transform 0.25s ease,
    opacity 0.25s ease;
}
.slide-x-enter-from,
.slide-x-leave-to {
  transform: translateX(100%);
  opacity: 0;
}
</style>
